/**
 * BaseballCard - Shareable player profile card with canvas export
 * 
 * Features:
 * - Avatar display
 * - Player name and level
 * - Career batting stats
 * - W-L record
 * - Export to PNG via canvas
 */

import { useRef, useState } from 'react';
import { Download, Share2 } from 'lucide-react';
import AvatarCanvas from './AvatarCanvas';
import { playClick, playSuccess } from '@/utils/sounds';
import { toast } from 'sonner';

export default function BaseballCard({ profile, onClose }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const {
    username = 'Player',
    level = 1,
    avatar_config = {},
    career_stats = {},
  } = profile || {};

  const {
    singles = 0,
    doubles = 0,
    triples = 0,
    home_runs = 0,
    rbi = 0,
    runs_scored = 0,
    outs = 0,
    wins = 0,
    losses = 0,
    games_played = 0,
    strikeouts_pitched = 0,
  } = career_stats;

  // Calculate batting average (simplified: hits / (hits + outs))
  const hits = singles + doubles + triples + home_runs;
  const atBats = hits + outs;
  const battingAvg = atBats > 0 ? (hits / atBats).toFixed(3).slice(1) : '.000';

  // Export card to PNG using canvas
  const exportToPNG = async () => {
    playClick();
    setExporting(true);

    try {
      // Create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Card dimensions
      const width = 400;
      const height = 600;
      canvas.width = width;
      canvas.height = height;

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#4B8B3B');
      gradient.addColorStop(1, '#2d5a24');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Border
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 8;
      ctx.strokeRect(4, 4, width - 8, height - 8);

      // Inner border
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = 3;
      ctx.strokeRect(12, 12, width - 24, height - 24);

      // Header area
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(20, 20, width - 40, 80);

      // Title
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 24px "Press Start 2P", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('RADIO BASEBALL', width / 2, 55);
      
      ctx.fillStyle = '#F5F5DC';
      ctx.font = '12px "Press Start 2P", monospace';
      ctx.fillText('PLAYER CARD', width / 2, 80);

      // Avatar area (placeholder - drawing simplified avatar)
      ctx.fillStyle = '#F5F5DC';
      ctx.fillRect(125, 110, 150, 200);
      
      // Draw simplified avatar representation
      ctx.fillStyle = avatar_config.primary_color || '#4B8B3B';
      ctx.fillRect(160, 140, 80, 100);
      ctx.fillStyle = '#FFDBB4'; // Skin
      ctx.fillRect(175, 120, 50, 50);

      // Player name
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(20, 320, width - 40, 50);
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 20px "Press Start 2P", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(username.toUpperCase().slice(0, 12), width / 2, 355);

      // Level badge
      ctx.fillStyle = '#CC0000';
      ctx.beginPath();
      ctx.arc(width - 60, 340, 30, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#F5F5DC';
      ctx.font = 'bold 16px "Press Start 2P", monospace';
      ctx.fillText(`L${level}`, width - 60, 345);

      // Stats section
      ctx.fillStyle = '#F5F5DC';
      ctx.fillRect(20, 380, width - 40, 200);
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 380, width - 40, 200);

      // Stats header
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 12px "Press Start 2P", monospace';
      ctx.textAlign = 'left';
      ctx.fillText('CAREER STATS', 35, 405);

      // Batting stats
      const statsY = 430;
      ctx.font = '10px "VT323", monospace';
      
      const drawStat = (label, value, x, y) => {
        ctx.fillStyle = '#666666';
        ctx.textAlign = 'left';
        ctx.fillText(label, x, y);
        ctx.fillStyle = '#1a1a1a';
        ctx.font = 'bold 14px "VT323", monospace';
        ctx.fillText(String(value), x, y + 18);
        ctx.font = '10px "VT323", monospace';
      };

      drawStat('AVG', battingAvg, 35, statsY);
      drawStat('HR', home_runs, 110, statsY);
      drawStat('RBI', rbi, 175, statsY);
      drawStat('RUNS', runs_scored, 240, statsY);
      
      drawStat('1B', singles, 35, statsY + 50);
      drawStat('2B', doubles, 110, statsY + 50);
      drawStat('3B', triples, 175, statsY + 50);
      drawStat('GP', games_played, 240, statsY + 50);

      // W-L Record
      drawStat('RECORD', `${wins}-${losses}`, 310, statsY);
      
      // Pitching (placeholder for future)
      ctx.fillStyle = '#999999';
      ctx.font = '8px "VT323", monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`K: ${strikeouts_pitched || '-'}`, 310, statsY + 68);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${username}_baseball_card.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        playSuccess();
        toast.success('Card saved!');
        setExporting(false);
      }, 'image/png');

    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export card');
      setExporting(false);
    }
  };

  // Share card (Web Share API if available)
  const shareCard = async () => {
    playClick();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${username}'s Radio Baseball Card`,
          text: `Check out my Radio Baseball stats! Level ${level}, ${wins}-${losses} record, ${home_runs} HRs!`,
          url: window.location.origin,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
        }
      }
    } else {
      // Fallback: copy stats to clipboard
      const text = `🎵⚾ ${username}'s Radio Baseball Card\nLevel ${level} | ${wins}-${losses} Record\n${home_runs} HRs | ${rbi} RBIs | AVG ${battingAvg}\nPlay at ${window.location.origin}`;
      await navigator.clipboard.writeText(text);
      toast.success('Stats copied to clipboard!');
    }
  };

  return (
    <div 
      className="baseball-card-modal fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        ref={cardRef}
        className="baseball-card max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="baseball-card"
      >
        {/* Card Design */}
        <div 
          className="rounded-lg overflow-hidden border-8 border-ink shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, #4B8B3B 0%, #2d5a24 100%)',
          }}
        >
          {/* Header */}
          <div className="bg-ink py-4 px-6">
            <h2 className="retro-heading text-lg text-yellow-400 text-center">
              RADIO BASEBALL
            </h2>
            <p className="retro-text text-xs text-cream text-center">
              PLAYER CARD
            </p>
          </div>

          {/* Avatar Section */}
          <div className="flex justify-center py-6">
            <div className="bg-cream p-4 border-4 border-ink">
              <AvatarCanvas config={avatar_config} size={120} />
            </div>
          </div>

          {/* Name & Level */}
          <div className="bg-ink py-3 px-6 flex items-center justify-between">
            <h3 className="retro-heading text-lg text-yellow-400 truncate flex-1">
              {username.toUpperCase()}
            </h3>
            <div className="bg-red-600 px-3 py-1 border-2 border-red-800 ml-4">
              <span className="retro-text text-xs text-cream">Lv{level}</span>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-cream p-4">
            <h4 className="retro-text text-xs text-ink mb-3">CAREER STATS</h4>
            
            {/* Main Stats Row */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="text-center">
                <div className="retro-body text-xs text-dirt">AVG</div>
                <div className="retro-heading text-lg text-ink">{battingAvg}</div>
              </div>
              <div className="text-center">
                <div className="retro-body text-xs text-dirt">HR</div>
                <div className="retro-heading text-lg text-ink">{home_runs}</div>
              </div>
              <div className="text-center">
                <div className="retro-body text-xs text-dirt">RBI</div>
                <div className="retro-heading text-lg text-ink">{rbi}</div>
              </div>
              <div className="text-center">
                <div className="retro-body text-xs text-dirt">RUNS</div>
                <div className="retro-heading text-lg text-ink">{runs_scored}</div>
              </div>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-5 gap-1 text-center mb-4">
              <div>
                <div className="retro-body text-xs text-dirt">1B</div>
                <div className="retro-text text-sm text-ink">{singles}</div>
              </div>
              <div>
                <div className="retro-body text-xs text-dirt">2B</div>
                <div className="retro-text text-sm text-ink">{doubles}</div>
              </div>
              <div>
                <div className="retro-body text-xs text-dirt">3B</div>
                <div className="retro-text text-sm text-ink">{triples}</div>
              </div>
              <div>
                <div className="retro-body text-xs text-dirt">GP</div>
                <div className="retro-text text-sm text-ink">{games_played}</div>
              </div>
              <div>
                <div className="retro-body text-xs text-dirt">W-L</div>
                <div className="retro-text text-sm text-ink">{wins}-{losses}</div>
              </div>
            </div>

            {/* Pitching Placeholder */}
            <div className="text-center pt-2 border-t-2 border-dirt/30">
              <span className="retro-body text-xs text-dirt">
                K: {strikeouts_pitched || '-'} (coming soon)
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={exportToPNG}
            disabled={exporting}
            className="pixel-btn flex-1 flex items-center justify-center gap-2"
            data-testid="download-card-btn"
          >
            <Download size={16} />
            {exporting ? 'SAVING...' : 'SAVE PNG'}
          </button>
          <button
            onClick={shareCard}
            className="pixel-btn flex-1 flex items-center justify-center gap-2"
            data-testid="share-card-btn"
          >
            <Share2 size={16} />
            SHARE
          </button>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="pixel-btn w-full mt-3"
          data-testid="close-card-btn"
        >
          CLOSE
        </button>
      </div>
    </div>
  );
}
